import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Container } from "reactstrap";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stepper,
  Step,
  StepButton,
  Typography,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Grid,
  Alert,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { QueryClient, QueryClientProvider, useQuery } from "react-query";
import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import BAVToggleButton from "./BAVToggleButton";
import BAVRadioGroup from "./BAVRadioGroup";
import BAVRadioGroupESG from "./BAVRadioGroupESG";
import BAVRadioGroupSparplan from "./BAVRadioGroupSparplan";
import BAVSlider from "./BAVSlider";
import BAVRisk from "./BAVRisk";
import BAVStrategy from "./BAVStrategy";
import BAVTextArea from "./BAVTextArea";
import PdfIcon from "@mui/icons-material/PictureAsPdfOutlined";
import "./CustomerProfile.css";
import { getUserToken } from "../utils/AuthTokenReader.jsx";
import BAVButton from "./BAVButton";
import { convertUTCDateToLocalDate } from "../utils/Util.jsx";
import { msalInstance } from "../index";
import { debugLog } from "../utils/Util";
import Tooltip from "@mui/material/Tooltip";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);

const queryClient = new QueryClient();

const CustomerProfile = () => {
  //const location = useLocation();
  return (
    <QueryClientProvider client={queryClient}>
      <BAVStepper />
    </QueryClientProvider>
  );
};
export default CustomerProfile;

function BAVStepper() {
  const navigate = useNavigate();
  const location = useLocation();
  const account = msalInstance.getActiveAccount();
  const sparplanInitializedRef = React.useRef(false);

  if (!location.state) {
    // Testmodus ohne Übergabeparameter
    var entrytype = 2;
    var entryid = "6";
    var readOnly = true;
  } else {
    var kd = location.state.kunde;
    var entrytype = kd.entryType;
    var entryid = kd.entryId;
    var readOnly = location.state.readOnly;
    }
  // Auth Token
  const [options, setOptions] = useState();
  const [token, setToken] = useState();
  const initToken = async () => {
    const ltoken = await getUserToken();
    setToken(ltoken);
    const headers = new Headers();
    const bearer = `Bearer ${ltoken.accessToken}`;
    headers.append("Authorization", bearer);
    headers.append("Content-Type", "application/json");
    setOptions({ headers: headers });
  };
  const reInitToken = new Promise((resolve, reject) => {
    if (token && token.expiresOn < Date.now()) {
      debugLog("Start initToken");
      initToken().then((res) => {
        debugLog("Finished initToken");
        resolve("initToken");
      });
    } else {
      debugLog("tokenValid");
      resolve("tokenValid");
    }
  });
  //debugLog ('Uebergabe: entrytype:'+entrytype+', entryid='+entryid);
  const {
    isLoading,
    data: allEntries,
    refetch,
  } = useQuery("ProfileFormData", fetchProfileFormData, {
    enabled: !!options,
    cacheTime: 0,
  });
  const [entries, setEntries] = useState();
  const [questions, setQuestions] = useState();
  const [responses, setResponses] = useState();
  const [activeStep, setActiveStep] = useState(0);
  const [isValid, setIsValid] = useState(true);
  // MissingMandatoryValue soll von Komponente gesetzt werden. Weiter wird dann unterdrückt und Fehlermeldung eingeblendet
  const [missingMandatoryValue, setMissingMandatoryValue] = useState({
    state: false,
    text: "",
  });
  const [disableAuswertung, setDisableAuswertung] = useState(false);
  const [investWithStandingOrder, setInvestWithStandingOrder] = useState("1");
  const [periodicityValue, setPeriodicityValue] = useState("1");
  const [standingOrderIntialized, setStandingOrderInitialized] =
    useState(false);
  const [bankAccount, setBankAccount] = useState("");
  const [customAccount, setCustomAccount] = useState("");
  const isCustomAccount = bankAccount === "99";
  const [soExcutionDate, setSoExcutionDate] = useState("28");
  const [startDateValue, setStartDateValue] = useState(null);
  const [endDateValue, setEndDateValue] = useState(null);

  const { isLoading2, data: accounts } = useQuery(
    "CustomerAccountData",
    fetchCustomerAccounts,
    { enabled: !!options, cacheTime: 0 },
  );
  const {
    isLoading3,
    data: isVV,
    refetch: refetchIsVV,
  } = useQuery("isVV", fetchisVV, { enabled: !!options, cacheTime: 0 });


  useEffect(() => {
    if (allEntries) {
      //debugLog('setAllEntries');
      setEntries(allEntries);
      var questions = getQuestions(allEntries);
      setQuestions(questions);
      var responses = getResponses(allEntries, questions);
      setResponses(responses);
    }
  }, [allEntries, activeStep]);

  useEffect(() => {
    initToken();
  }, []);

  useEffect(() => {
    if (entries) {
      validateKEEntries();
    }
  }, [entries]);

  useEffect(() => {
    const q = questions?.[activeStep];
    const v2 = responses?.[0]?.value_2;

    const hasValue2 =
      v2 != null && // nicht null / undefined
      v2.toString().trim() !== ""; // nicht leer

    if (
      ((hasValue2 && v2 !== "0") || (!hasValue2 && !standingOrderIntialized)) &&
      ((q?.entryType === 2 && q?.questionId === 15 && isVVSelected(entries)) ||
        (q?.entryType === 3 && q?.questionId === 25))
    ) {
      setInvestWithStandingOrder("1");
      setStandingOrderInitialized(true);
    } else {
      setInvestWithStandingOrder("0");
      setStandingOrderInitialized(false);
    }
  }, [questions, responses, activeStep, entries]);

  useEffect(() => {
    const v3 = responses?.[0]?.value_3;
    if (typeof v3 === "string" && v3.includes(";")) {
      const raw = String(v3 ?? "").split(";");
      while (raw.length && raw[raw.length - 1] === "") raw.pop();

      const [pRaw = "", aRaw = "", dRaw = "28", dtsRaw = null, dteRaw = null] =
        raw;
      const toNull = (v) => (v === "" ? null : v);

      const p = pRaw;
      const a = aRaw;
      const d = dRaw;
      const dts = toNull(dtsRaw);
      const dte = toNull(dteRaw);

      //robustes Parsen der Datumsstrings zu dayjs (unterstützt ISO + DD/MM/YYYY)
      const parseDate = (val) =>
        val
          ? dayjs(val, ["YYYY-MM-DD", "DD/MM/YYYY"], true) // strict parsing
          : null;

      const startDt = parseDate(dts);
      const endDt = parseDate(dte);
      setPeriodicityValue(p || "1");
      setSoExcutionDate(d || "28");
      setStartDateValue(startDt && startDt.isValid() ? startDt : null);
      setEndDateValue(endDt && endDt.isValid() ? endDt : null);
      const fixedOptions = new Set(accounts?.map((acc) => String(acc.ktLnr)));

      if (!Array.isArray(accounts) && isLoading2) {
        // Accounts sind noch nicht da -> nur Defaults setzen und später erneut laufen lassen
        setBankAccount("");
        setCustomAccount("");
        // früh returnen, bis accounts verfügbar sind
        return;
      }
      if (a && !fixedOptions.has(String(a)) && a !== "98") {
        setBankAccount("99");
        setCustomAccount(a);
        setSoExcutionDate(d);
        setStartDateValue(startDt && startDt.isValid() ? startDt : null);
        setEndDateValue(endDt && endDt.isValid() ? endDt : null);
      } else {
        setBankAccount(a || "");
        setCustomAccount("");
        setSoExcutionDate(d);
        setStartDateValue(startDt && startDt.isValid() ? startDt : null);
        setEndDateValue(endDt && endDt.isValid() ? endDt : null);
      }
    } else {
      setPeriodicityValue("1");
      setBankAccount("");
      setCustomAccount("");
      setSoExcutionDate("28");
      setStartDateValue(null);
      setEndDateValue(null);
    }
  }, [responses]);

  useEffect(() => {
      if (missingMandatoryValue.state && isStandingOrderSectionValid() && isSparplanOtherPersonVaid()) {
        setMissingMandatoryValue({ state: false, text: "" });
    }
  }, [
    investWithStandingOrder,
    entries, // ⬅️ WICHTIG: auf entries hören, nicht (nur) responses
    periodicityValue,
    bankAccount,
    customAccount,
    soExcutionDate,
  ]);

    useEffect(() => {
        if (sparplanInitializedRef.current) return;

        const response = responses?.[0];
        if (!response) return;

        const index = entries.findIndex(
            (e) => e.responseId === response.responseId
        );

        const entry = entries[index];

        const hasNoDbEntry =
            !entry || !entry.entryDetailId || entry.entryDetailId === "0";

        if (hasNoDbEntry && investWithStandingOrder === "1") {
            sparplanInitializedRef.current = true;

            handleChangeSparplan(
                {
                    response,
                    valueField: "value_2",
                },
                250 // Defaultwert
            );
        }
    }, [responses, entries, investWithStandingOrder]);

  /**
   * **************************************************************
   *
   * VARIABLEN
   *
   * **************************************************************
   */

  // Diese Variablen müssen von Parent übergeben werden

  // Beispiel Kundenprofil

  //var entrytype = 1;
  //var entryid = "5";

  // Beispiel Portfolioprofil
  //var entrytype = 2;
  //var entryid = "6";

  let arrInit = [];

  /**
   * **************************************************************
   *
   * KONSTANTEN
   *
   * **************************************************************
   */

  const defaultColStyle = {
    maxWidth: "100px",
    wordWrap: "break-word",
  };

  const combinedColStyle = {
    maxWidth: "200px",
    wordWrap: "break-word",
  };

  /**
   *
   * Handlingsfunktionen des Steppers
   *
   */
  const handleNext = () => {
    if (missingMandatoryValue.state) {
      return;
    }
    if ([15, 25].includes(questions[activeStep].questionId)) {
      // Custom-Konto unvollständig/ungespeichert?
      if (isCustomAccountIncomplete()) {
        setMissingMandatoryValue({
          state: true,
          text: "Bitte geben Sie ein Konto ein und speichern Sie es über «Übernehmen», bevor Sie fortfahren.",
        });
        return; // Schrittwechsel verhindern
      }

      if (!isStandingOrderSectionValid()) {
        setMissingMandatoryValue({
          state: true,
          text: "Für den Dauerauftrag sind die Felder Betrag, Periodizität und Belastungskonto erforderlich.",
        });
        return;
      }
    }

    setMissingMandatoryValue({ state: false, text: "" });
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    if (missingMandatoryValue.state) {
      return;
    }
    setMissingMandatoryValue({ state: false, text: "" });
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleReset = () => {
    setMissingMandatoryValue({ state: false, text: "" });
    setActiveStep(0);
  };
  const handleBackToCustomer = () => {
    navigate(-1);
  };

  const handleAuswertung = () => {
    window.open("/BAVReport?entryId=" + entryid);
  };

  /**
   * Die ResponseTypes
   *
   *    1 = Auswahlliste
   *    2 = Multiselect
   *    3 = Slider kleine Schritte
   *    4 = Slider grössere Schritte
   *    5 = Auswahlliste mit Vorschlag/Berechnung
   *    6 = Risikoprofil
   *    7 = Bemerkungen
   *    8 = Auswahlliste spezial ESG
   *    *
   */

  const responseType1Component = () => {
    return (
      <>
        <Container fluid>
          <BAVRadioGroup
            responses={responses}
            callback={handleRadioGroupChange}
            key={responses[0].questionId}
            readOnly={readOnly}
          />
        </Container>
      </>
    );
  };

  const responseType2Component = (pEntryType) => {
    /*
          responses.map((_response, _index) => (
            debugLog(_response)
        
        ));
        */
    var kes = [8, 9, 10, 11, 13];
    return (
      <TableContainer>
        <Table aria-label="responseTable">
          <TableHead>
            <TableRow>
              <TableCell style={{ minWidth: "250px" }}>BEZEICHNUNG</TableCell>
              <TableCell align="left" style={combinedColStyle}>
                KENNTNISSE & ERFAHRUNG VORHANDEN
              </TableCell>
              <TableCell align="left" style={defaultColStyle}>
                AUFKLÄRUNG ERHALTEN
              </TableCell>
              <TableCell align="left" style={defaultColStyle}>
                PRODUKT-INFORMATIONSBLATT
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {responses.map((_response, _index) => (
              <TableRow key={_response.responseId}>
                {kes.includes(_response.responseId) && (
                  <Tooltip title="K&E muss neu eingeholt werden">
                    <TableCell
                      key={_response.responseId + "A"}
                      component="th"
                      scope="row"
                      style={{ minWidth: "250px" }}
                    >
                      <span className="bavHighlight">{_response.response}</span>
                    </TableCell>
                  </Tooltip>
                )}
                {!kes.includes(_response.responseId) && (
                  <TableCell
                    key={_response.responseId + "A"}
                    component="th"
                    scope="row"
                    style={{ minWidth: "250px" }}
                  >
                    <span>{_response.response}</span>
                  </TableCell>
                )}
                <TableCell
                  key={_response.responseId + "B"}
                  align="center"
                  style={combinedColStyle}
                >
                  <BAVToggleButton
                    buttonId={"btnr_1_" + _response.responseId}
                    questionid={_response.questionId}
                    responseid={_response.responseId}
                    button="1"
                    value_1={getButtonValue(1, _response.responseId)}
                    value_3={getButtonValue(3, _response.responseId)}
                    callback={handleButtonChange}
                    init={initButton}
                    entryType={pEntryType}
                    readOnly={readOnly}
                  />
                </TableCell>

                <TableCell
                  key={_response.responseId + "D"}
                  align="left"
                  style={defaultColStyle}
                >
                  <BAVToggleButton
                    buttonId={"btnr_3_" + _response.responseId}
                    questionid={_response.questionId}
                    responseid={_response.responseId}
                    button="3"
                    value_1={getButtonValue(1, _response.responseId)}
                    value_3={getButtonValue(3, _response.responseId)}
                    callback={handleButtonChange}
                    init={initButton}
                    readOnly={readOnly}
                  />
                </TableCell>
                <TableCell
                  key={_response.responseId + "E"}
                  align="left"
                  style={defaultColStyle}
                >
                  <PdfIcon
                    onClick={(event) => {
                      window.open(
                        import.meta.env.VITE_REACT_APP_ABP_DOCUMENT_URL +
                          _response.responseValue,
                        "_blank",
                        "popup=true,resizable=yes,top=500,width=800,height=800,left=100",
                      );
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const responseType3Component = (pEntryType) => {
    if ((pEntryType == 2 && isVVSelected(entries)) || pEntryType == 3) {
      return (
        <>
          <BAVSlider
            min={0}
            max={
              responses[0].responseValue != ""
                ? parseInt(responses[0].responseValue)
                : 500000
            }
            step={5000}
            response={responses[0]}
            key={responses[0].responseId}
            callback={handleSliderChange}
            readOnly={readOnly}
          />
          <Box sx={{ width: "100%" }} className="bavSlider">
            <Grid container spacing={20} alignItems="center">
              <Grid item xs={8}>
                <b>
                  Möchten Sie mit einem Dauerauftrag regelmässig investieren?
                </b>
              </Grid>
              <Grid item xs={4}>
                <FormControl>
                  <RadioGroup
                    row
                    value={investWithStandingOrder || ""}
                    onChange={handleInvestWithStandingOrder}
                    aria-labelledby="bav-row-radio-buttons-group-label"
                    name="bav-row-radio-buttons-group"
                    disabled={readOnly}
                  >
                    <FormControlLabel
                      value="1"
                      control={<Radio disabled={readOnly} />}
                      label="Ja"
                    />
                    <FormControlLabel
                      value="0"
                      control={<Radio />}
                      disabled={readOnly}
                      label="Nein"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
          {investWithStandingOrder == "1" && (
            <Box
              sx={{
                width: "90%",
                borderRadius: "20px",
                border: "1px solid #cbd1d663",
                marginLeft: "20px",
                typography: "body2",
              }}
              className="bavSlider"
            >
              <BAVSlider
                min={50}
                max={10000}
                step={50}
                response={responses[0]}
                key={responses[0].responseId + "1"}
                valueField="value_2"
                title={"Betrag"}
                callback={handleChangeSparplan}
                readOnly={readOnly}
                sliderWidth={300}
                defaultValue={250}
              />
              <FormControl sx={{ m: 1, minWidth: 180 }} size="small">
                <InputLabel id="lblPeriodicity">
                  <Typography variant="body2">Periodizität</Typography>
                </InputLabel>
                <Select
                  labelId="lblPeriodicity"
                  id="cmbPeriodicity"
                  value={periodicityValue}
                  label="Periodizität"
                  onChange={(e) => handlePeriodicityChange(e)}
                  name="cmbPeriodicity"
                  disabled={readOnly}
                >
                  <MenuItem value={"1"}>
                    <Typography variant="body2">monatlich</Typography>
                  </MenuItem>
                  <MenuItem value={"2"}>
                    <Typography variant="body2">quartalsweise</Typography>
                  </MenuItem>
                  <MenuItem value={"3"}>
                    <Typography variant="body2">halbjährlich</Typography>
                  </MenuItem>
                  <MenuItem value={"4"}>
                    <Typography variant="body2">jährlich</Typography>
                  </MenuItem>
                </Select>
              </FormControl>

              <FormControl sx={{ m: 1, minWidth: 700 }} size="small">
                <InputLabel id="lblAccount">
                  <Typography variant="body2">
                    Belastungskonto Dauerauftrag
                  </Typography>
                </InputLabel>
                <Select
                  labelId="lblAccount"
                  id="cmbAccount"
                  value={bankAccount}
                  onChange={handleAccountChange}
                  label="Belastungskonto Dauerauftrag"
                  name="cmbAccount"
                  disabled={readOnly}
                >
                  <MenuItem disabled disableGutters>
                    <Box
                      sx={{
                        px: 2, // horizontaler Innenabstand
                        display: "grid",
                        gridTemplateColumns: "150px 190px 210px 50px 100px",
                        columnGap: 2,
                        width: 1,
                        color: "text.secondary",
                        fontWeight: 600,
                        typography: "caption",
                      }}
                    >
                      <span>Konto</span>
                      <span>Rubrik</span>
                      <span>Kontoart</span>
                      <span>Währung</span>
                      <Box component="span" sx={{ textAlign: "right" }}>
                        Saldo
                      </Box>
                    </Box>
                  </MenuItem>

                  {accounts?.map((acc) => (
                    <MenuItem value={acc.ktLnr} key={acc.ktLnr} disableGutters>
                      <Box
                        sx={{
                          px: 2,
                          display: "grid",
                          gridTemplateColumns: "150px 190px 210px 50px 100px",
                          columnGap: 2,
                          alignItems: "center",
                          width: 1,
                        }}
                      >
                        <Typography variant="body2">{acc.editKtNr}</Typography>
                        <Typography variant="body2">{acc.rubrik}</Typography>
                        <Typography variant="body2">{acc.ktArtBez}</Typography>
                        <Typography variant="body2" noWrap>
                          {acc.wrgBez}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {acc.saldoKWrg}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                  <MenuItem value="99" key="99">
                    <Typography variant="body2">
                      Anderes bestehendes BAV-Konto
                    </Typography>
                  </MenuItem>
                  <MenuItem value="98" key="98">
                    <Typography variant="body2">
                      Neu zu eröffnendes Konto gemäss Orion-Auftrag
                    </Typography>
                  </MenuItem>
                </Select>
              </FormControl>
              {isCustomAccount && (
                <>
                  <Box sx={{ display: "flex", alignItems: "left", m: 1 }}>
                    <TextField
                      id="txtCustomAccount"
                      label="Anderes Konto"
                      variant="outlined"
                      size="small"
                      value={customAccount}
                      onChange={(e) => {
                        const val = e.target.value;
                        // nur Zahlen und '.' erlauben
                        const filtered = val
                          .replace(/[^0-9.]/g, "")
                          .slice(0, 16);
                        setCustomAccount(filtered);
                      }}
                      disabled={readOnly}
                      inputProps={{
                        // ← direkt am <input>
                        maxLength: 16,
                        inputMode: "decimal",
                        pattern: "[0-9.]*",
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{ minWidth: 240 }}
                      InputProps={{
                        sx: {
                          typography: "body2",
                          "& .MuiOutlinedInput-notchedOutline legend": {
                            width: "90px",
                            maxWidth: "100%",
                          },
                        },
                      }}
                    />

                    <BAVButton
                      variant="contained"
                      size="small"
                      onClick={() => handleSaveCustomAccount()}
                      disabled={
                        readOnly ||
                        customAccount.trim() === "" ||
                        !isCustomAccountIncomplete()
                      }
                      sx={{ ml: 1 }}
                    >
                      Übernehmen
                    </BAVButton>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "left", m: 1 }}>
                    <div style={{ fontSize: 10, marginBottom: 10 }}>
                      <i>
                        Handelt es sich beim Belastungskonto um ein Konto eines
                        anderen Partners ist eine vorhandene Vollmacht zwingend
                      </i>
                    </div>
                  </Box>
                </>
              )}

              <Box sx={{ display: "flex", alignItems: "start", gap: 1, m: 1 }}>
                <Box component="div" sx={{ fontWeight: 700, minWidth: 180 }}>
                  Ausführungstag
                </Box>

                <Box component="div" sx={{ minWidth: 70 }}>
                  jeweils am
                </Box>
                <TextField
                  id="txtExcutionDate"
                  variant="standard"
                  size="small"
                  value={soExcutionDate}
                  onChange={(e) => handleSODateChange(e)}
                  onBlur={handleSOBlur}
                  inputProps={{
                    // ← direkt am <input>
                    maxLength: 2,
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                  disabled={readOnly}
                  sx={{
                    maxWidth: 20,
                    alignSelf: "flex-start",
                    "& .MuiInputBase-input": {
                      fontSize: 12,
                      paddingY: 0, // extra kompakt
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        disablePointerEvents
                        sx={{
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          ml: 0,
                        }}
                      >
                        <Typography
                          sx={{ fontSize: "inherit", lineHeight: "inherit" }}
                        >
                          .
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />

                <Box
                  component="div"
                  sx={{
                    display: "inline-block", // macht den Block als Einheit
                    whiteSpace: "normal", // Block selbst darf intern umbrechen (bei schmalen Layouts)
                    // optional: für sehr lange Texte Worttrennung erlauben
                    overflowWrap: "anywhere",
                  }}
                >
                  eines Monats (in Abhängigkeit der oben angegebenen
                  Ausführungsperiodizität)
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, m: 1 }}>
                <Box component="div" sx={{ fontWeight: 400, minWidth: 180 }}>
                  Startdatum
                </Box>
                <Box sx={{ minWidth: 300 }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={startDateValue}
                      onChange={(newValue) => handleStartDateChange(newValue)}
                      //onBlur={handleStartDateBlur}
                      format="DD/MM/YYYY"
                      slotProps={{
                        textField: {
                          placeholder: "nächstmögliche Ausführung",
                          fullWidth: true,
                          size: "small",
                          label: undefined, // explizit KEIN Label

                          InputLabelProps: { shrink: false },

                          sx: {
                            "& .MuiInputBase-input::placeholder": {
                              color: "black",
                              opacity: 1,
                              fontSize: "0.875rem",
                            },
                            "& .MuiInputBase-input": {
                              backgroundColor: "transparent", // falls bei dir grauer BG sichtbar war
                              fontSize: "0.875rem",
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Box>
                <BAVButton
                  size="small"
                  variant="text"
                  onClick={() => {
                    setStartDateValue(null);
                    saveStandingOrderSettings(
                      periodicityValue,
                      bankAccount,
                      soExcutionDate,
                      null,
                      endDateValue,
                      customAccount?.trim(),
                    );
                  }}
                  disabled={!startDateValue}
                >
                  löschen
                </BAVButton>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, m: 1 }}>
                <Box component="div" sx={{ fontWeight: 400, minWidth: 180 }}>
                  Enddatum
                </Box>
                <Box sx={{ minWidth: 300 }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={endDateValue}
                      onChange={(newValue) => handleEndDateChange(newValue)}
                      //onBlur={handleEndDateBlur}
                      format="DD/MM/YYYY"
                      slotProps={{
                        textField: {
                          placeholder: "bis auf Widerruf",
                          fullWidth: true,
                          size: "small",
                          label: undefined, // explizit KEIN Label
                          InputLabelProps: { shrink: false },
                          sx: {
                            "& .MuiInputBase-input::placeholder": {
                              color: "black",
                              opacity: 1,
                              fontSize: "0.875rem",
                            },
                            "& .MuiInputBase-input": {
                              backgroundColor: "transparent",
                              fontSize: "0.875rem",
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Box>
                <BAVButton
                  size="small"
                  variant="text"
                  onClick={() => {
                    setEndDateValue(null);
                    saveStandingOrderSettings(
                      periodicityValue,
                      bankAccount,
                      soExcutionDate,
                      startDateValue,
                      null,
                      customAccount?.trim(),
                    );
                  }}
                  disabled={!endDateValue}
                >
                  löschen
                </BAVButton>
              </Box>
            </Box>
          )}
        </>
      );
    }
    return (
      <BAVSlider
        min={0}
        max={
          responses[0].responseValue != ""
            ? parseInt(responses[0].responseValue)
            : 500000
        }
        step={5000}
        response={responses[0]}
        key={responses[0].responseId}
        callback={handleSliderChange}
        readOnly={readOnly}
      />
    );
  };

  const responseType4Component = () => {
    return (
      <BAVSlider
        min={0}
        max={
          responses[0].responseValue != "" ? responses[0].responseValue : 500000
        }
        step={5000}
        response={responses[0]}
        key={responses[0].responseId}
        callback={handleSliderChange}
        readOnly={readOnly}
      />
    );
  };
  const responseType5Component = () => {
    return (
      <>
        <Container fluid>
          <BAVRadioGroup
            responses={responses}
            callback={handleRadioGroupChange}
            invRec={responses[0].questionId == 14 ? 1 : 0}
            invStrat={
              responses[0].questionId == 20 ||
              responses[0].questionId == 31 ||
              responses[0].questionId == 39
                ? 1
                : 0
            }
            invClientRisk={responses[0].questionId == 9 ? 1 : 0}
            key={responses[0].questionId}
            readOnly={readOnly}
          />
        </Container>
      </>
    );
  };
  const responseType6Component = () => {
    return (
      <BAVRisk
        readOnly={readOnly}
        response={responses[0]}
        callback={handleRiskChange}
      />
    );
  };
  const responseType7Component = () => {
    const handleValidation = (isValid) => {
      setIsValid(isValid);
    };
    var adminBemerkung =
      responses[0].value_2 ==
      "Durch Team Investment Solutions hinzugefügte Bemerkungen";
    return (
      <>
        <Container fluid>
          <Typography mt={1} variant="subtitle2" sx={{ fontWeight: "light" }}>
            {responses[0].value_2}
          </Typography>
          <Typography mt={1} variant="subtitle2" sx={{ fontWeight: "light" }}>
            {responses[0].value_3}{" "}
          </Typography>
          <BAVTextArea
            responses={responses}
            callback={handleTextAreaChange}
            key={responses[0].questionId}
            required={
              responses[0].value_2 || responses[0].value_3 ? true : false
            }
            validation={handleValidation}
            readOnly={readOnly || adminBemerkung}
          />
        </Container>
      </>
    );
  };
  const responseType8Component = () => {
    return (
      <>
        <Container fluid>
          <BAVRadioGroupESG
            responses={responses}
            callback={handleRadioGroupESGChange}
            key={responses[0].questionId}
            invRec={1}
            readOnly={readOnly}
          />
        </Container>
      </>
    );
  };

  const responseType9Component = () => {
    var settings = ["1", "1"];
    if (responses[0].responseValue != "") {
      settings = responses[0].responseValue.split(";");
      if (settings.length == 1) {
        settings[1] = "2000";
      }
    }
    return (
      <>
        <BAVSlider
          min={0}
          max={parseInt(settings[0])}
          step={1000}
          response={responses[0]}
          key={responses[0].responseId}
          callback={handleChangeSparplan}
          title={"Einmalig"}
          valueField={"value_1"}
          readOnly={readOnly}
        />
        <BAVSlider
          min={0}
          max={parseInt(settings[1])}
          step={50}
          response={responses[0]}
          key={responses[0].responseId + "1"}
          callback={handleChangeSparplan}
          title={"Wiederkehrend"}
          valueField={"value_2"}
          readOnly={readOnly}
        />
        <Box sx={{ width: "100%" }} className="bavSlider">
          <Grid container spacing={20} alignItems="center">
            <Grid item xs={2}>
              Periodizität
            </Grid>
            <Grid item xs={8}>
              <FormControl>
                <RadioGroup
                  row
                  aria-labelledby="bav-row-radio-buttons-group-label"
                  name="bav-row-radio-buttons-group"
                  value={responses[0].value_3}
                  onChange={(e) => handlePeriodicityChange(e)}
                  disabled={readOnly}
                >
                  <FormControlLabel
                    value="1"
                    control={<Radio disabled={readOnly} />}
                    label="monatlich"
                  />
                  <FormControlLabel
                    value="2"
                    control={<Radio />}
                    disabled={readOnly}
                    label="zweimonatlich"
                  />
                  <FormControlLabel
                    value="3"
                    control={<Radio />}
                    disabled={readOnly}
                    label="quartalsweise"
                  />
                  <FormControlLabel
                    value="4"
                    control={<Radio />}
                    disabled={readOnly}
                    label="halbjährlich"
                  />
                  <FormControlLabel
                    value="5"
                    control={<Radio />}
                    disabled={readOnly}
                    label="jährlich"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </>
    );
  };

  const responseType10Component = () => {
    return (
      <>
        <Container fluid>
          <BAVRadioGroupSparplan
            responses={responses}
            callback={handleRadioGroupSparplanChange}
            key={responses[0].questionId}
            invRec={1}
            setMissingMandatoryValue={setMissingMandatoryValue}
            readOnly={readOnly}
          />
        </Container>
      </>
    );
  };
  const responseType11Component = () => {
    return (
      <>
        <BAVStrategy
          responses={responses}
          callback={handleRadioGroupChange}
          readOnly={readOnly}
        ></BAVStrategy>
      </>
    );
  };
  // EntryType für für die Fragen Typ 2 verwendet, da diese auf Kunden und Portfolioebene angezeigt werden können
  const renderResponses = (pEntryType) => {
    // In der Konfiguration kann pro Seite nur der gleiche ResponseType verwendet werden.
    if (responses.length == 0) {
      return;
    }
    if (responses[0].responseType != 7) {
      if (!isValid) {
        setIsValid(true);
      }
    }
    switch (responses[0].responseType) {
      case 1:
        return responseType1Component();
        break;
      case 2:
        return responseType2Component(pEntryType);
        break;
      case 3:
        return responseType3Component(pEntryType);
        break;
      case 4:
        return responseType4Component();
        break;
      case 5:
        return responseType5Component();
        break;
      case 6:
        return responseType6Component();
        break;
      case 7:
        return responseType7Component();
        break;
      case 8:
        return responseType8Component();
        break;
      case 9:
        return responseType9Component();
        break;
      case 10:
        return responseType10Component();
        break;
      case 11:
        return responseType11Component();
        break;
    }
  };

  const renderCurrentStep = () => {
    const currentQuestion = questions[activeStep];
    //debugLog('renderCurrentStep');
    if (!currentQuestion) {
      return "Keine Konfigurationen für Entry Typ: " + entrytype;
    }
    // Spezialfall für Frage 14 "Wieviel Geld möchten Sie investieren?"
    if (
      (currentQuestion.entryType == 2 &&
        currentQuestion.questionId == 15 &&
        isVVSelected(entries)) ||
      (currentQuestion.entryType == 3 && currentQuestion.questionId == 25)
    ) {
      currentQuestion.question =
        "Wieviel Geld möchten Sie investieren? (Erstinvestition)";
    }
    return (
      <>
        <main className="main">
          <Box className="area">
            <div>
              {currentQuestion.entryType == 1
                ? "KUNDENEBENE"
                : currentQuestion.entryType == 2
                  ? "PORTFOLIO"
                  : currentQuestion.entryType == 3
                    ? "SPARPLAN"
                    : currentQuestion.entryType == 4
                      ? "VORSORGE"
                      : ""}
            </div>
            <hr />
          </Box>
          <Box className="question">
            <div>
              <b>{currentQuestion.question}</b>
            </div>
          </Box>
          <Box className="response">
            {renderResponses(currentQuestion.entryType)}
          </Box>
          {missingMandatoryValue.state && (
            <>
              <p />
              <Alert severity="error">{missingMandatoryValue.text}</Alert>
            </>
          )}
        </main>
        <footer className="footer">
          <Box sx={{ flex: "1 1 auto" }} />
          <div className="buttonContainer">
            <BAVButton variant="outlined" onClick={handleBackToCustomer}>
              Zurück zum Kunden
            </BAVButton>
            <Tooltip
              title={
                disableAuswertung ? "Bitte alle nötigen K&E aufklären" : ""
              }
            >
              <span>
                <BAVButton
                  variant="outlined"
                  onClick={handleAuswertung}
                  disabled={disableAuswertung}
                >
                  {" "}
                  Auswertung
                </BAVButton>
              </span>
            </Tooltip>

            <BAVButton
              variant="contained"
              onClick={handleBack}
              disabled={activeStep === 0 || missingMandatoryValue.state}
            >
              Zurück
            </BAVButton>
            <BAVButton
              variant="contained"
              onClick={handleNext}
              disabled={
                activeStep === totalSteps - 1 || missingMandatoryValue.state
              }
            >
              Weiter
            </BAVButton>
            <Box mr={18.3} hidden={activeStep == totalSteps - 1} />
            <BAVButton
              variant="closing"
              hidden={activeStep !== totalSteps - 1 || readOnly}
              onClick={handleSetProfileActive}
              disabled={!isValid}
            >
              Abschliessen
            </BAVButton>
          </div>
        </footer>
      </>
    );
  };

  /**
   * *************************************************************
   *
   * FUNKTIONEN
   *
   * *************************************************************
   */

  function fetchProfileFormData() {
    try {
      return reInitToken.then((res) => {
        return fetch("/profileform?entryid=" + entryid, options).then((res) => {
          return res.json();
        });
      });
    } catch (e) {
      debugLog(e);
      initToken();
    }
  }
  function fetchCustomerAccounts() {
    try {
      return reInitToken.then((res) => {
        // pMode 2 , allen Konten ausser VV
        return fetch("/account?pEntryId=" + entryid + "&pMode=2", options).then(
          (res) => {
            return res.json();
          },
        );
      });
    } catch (e) {
      initToken();
    }
  }

  function fetchisVV() {
    try {
      return reInitToken.then((res) => {
        // pMode 3 , nur VVKonten
        return fetch("/profileform/isVV/" + entryid, options).then((res) => {
          return res.json();
        });
      });
    } catch (e) {
      initToken();
    }
  }

  function fetchvalidateKE() {
    return reInitToken
      .then(() => {
        return fetch("/profileform/validateKE/" + entryid, options);
      })
      .then((res) => res.json()) // <– wichtig!
      .catch((err) => {
        initToken();
        throw err;
      });
  }

  function saveChange(postData) {
    if (readOnly) {
      return;
    }
    if (postData.entryDetailId > 0) {
      //debugLog(postData);
      let requestOptions = {
        method: "PUT",
        headers: options.headers,
        body: JSON.stringify(postData),
      };
      reInitToken.then((res) => {
        fetch("/entrydetails/" + postData.entryDetailId, requestOptions)
          .then(() => {
            refetch();
            refetchIsVV();
          })
          .catch((error) => {
            refetch();
            refetchIsVV();
          });
      });
    } else {
      //debugLog(postData);
      let requestOptions = {
        method: "POST",
        headers: options.headers,
        body: JSON.stringify(postData),
      };
      reInitToken.then((res) => {
        fetch("/entrydetails", requestOptions)
          .then(() => {
            refetch();
            refetchIsVV();
          })
          .catch((error) => {
            refetch();
            refetchIsVV();
          });
      });
    }
  }

  function handleRiskChange(props, newValue) {
    var index = entries.findIndex(
      (d) => d.responseId == props.response.responseId,
    );
    var entry = entries[index];
    if (!entry || !entry.entryDetailId) {
      entry = {
        entryDetailId: "0",
        entryId: props.response.entryId,
        entryDetailType: entrytype,
        questionId: props.response.questionId,
        responseId: props.response.responseId,
        value_1: newValue + "",
      };
    } else {
      entry["value_1"] = newValue + "";
    }
    var lEntries = [...entries];
    lEntries[index] = entry;
    setEntries(lEntries);
    saveChange(entry);
  }

  const handleStep = (step) => () => {
    if (missingMandatoryValue.state) {
      return;
    }
    setMissingMandatoryValue({ state: false, text: "" });
    setActiveStep(step);
  };
  function handleSliderChange(props, newValue) {
    //debugLog('handleSliderChange');
    //debugLog(newValue);
    //debugLog(props.response);
    var index = entries.findIndex(
      (d) => d.responseId == props.response.responseId,
    );
    var entry = entries[index];
    if (!entry || !entry.entryDetailId) {
      entry = {
        entryDetailId: "0",
        entryId: props.response.entryId,
        entryDetailType: entrytype,
        questionId: props.response.questionId,
        responseId: props.response.responseId,
        value_1: newValue + "",
      };
    } else {
      entry["value_1"] = newValue + "";
    }
    var lEntries = [...entries];
    lEntries[index] = entry;
    setEntries(lEntries);
    saveChange(entry, lEntries);
  }

  function handleChangeSparplan(props, newValue) {
    var valueField = props.valueField ? props.valueField : "value_1";
    var index = entries.findIndex(
      (d) => d.responseId == props.response.responseId,
    );
    var entry = entries[index];
    if (!entry || !entry.entryDetailId) {
      entry = {
        entryDetailId: "0",
        entryId: props.response.entryId,
        entryDetailType: entrytype,
        questionId: props.response.questionId,
        responseId: props.response.responseId,
        value_1: entry?.value_1 ?? "0", // <-- WICHTIG: immer setzen!
      };
    }

    if (newValue != null && newValue !== "") {
      entry[valueField] = String(newValue);
    }

    var lEntries = [...entries];
    lEntries[index] = entry;
    setEntries(lEntries);
    saveChange(entry, lEntries);
  }
  function handleTextAreaChange(props, textValue, response) {
    // nie speichern, wenn der Text nicht valide ist

    if (!isValid) {
      return;
    }
    var index = entries.findIndex(
      (d) =>
        d.questionId == response.questionId &&
        d.responseId == response.responseId &&
        d.entryDetailId,
    );
    var entry = entries[index];
    //debugLog(entry);
    if (!entry || !entry.entryDetailId) {
      entry = {
        entryDetailId: "0",
        entryId: response.entryId,
        entryDetailType: entrytype,
        questionId: response.questionId,
        responseId: response.responseId,
        value_1: textValue,
      };
    } else {
      entry["responseId"] = response.responseId;
      entry["value_1"] = textValue;
    }
    var lEntries = [...entries];
    lEntries[index] = entry;
    setEntries(lEntries);
    saveChange(entry);
  }

  function handleRadioGroupESGChange(props, value, response, component) {
    if (component.tagName == "TEXTAREA") {
      handleTextAreaChange(props, value, response);
    } else {
      handleRadioGroupChange(props, value, response);
    }
  }
  function handleStartDateChange(newValue) {
    setStartDateValue(newValue);
    saveStandingOrderSettings(
      periodicityValue,
      bankAccount,
      soExcutionDate,
      newValue,
      endDateValue,
      customAccount?.trim(),
    );
  }
  function handleEndDateChange(newValue) {
    setEndDateValue(newValue);
    saveStandingOrderSettings(
      periodicityValue,
      bankAccount,
      soExcutionDate,
      startDateValue,
      newValue,
      customAccount?.trim(),
    );
  }

  function isValidDate(dateString) {
    // Prüfung ob Datum im Format DD.MM.YYYY gültig ist
    let parts = dateString.split(".");
    if (parts.length !== 3) {
      return false;
    }
    let day = parseInt(parts[0], 10);
    let month = parseInt(parts[1], 10) - 1; // Monate sind von 0 bis 11
    let year = parseInt(parts[2], 10);
    let date = new Date(year, month, day);
    if (
      date.getDate() === day &&
      date.getMonth() === month &&
      date.getFullYear() === year
    ) {
      return true;
    } else {
      return false;
    }
  }
  function handleRadioGroupSparplanChange(props, value, response, component) {
    if (component.id == "bavDateField") {
      if (!isValidDate(value)) {
        value = "";
      }
      handleTextAreaChange(props, value, response);
    } else {
      handleRadioGroupChange(props, value, response);
    }
  }

  function handleRadioGroupChange(props, radioGroupValue, response) {
    var index = entries.findIndex(
      (d) => d.questionId == response.questionId && d.entryDetailId,
    );
    var entry = entries[index];
    //debugLog(entry);
    if (!entry) {
      entry = {
        entryDetailId: "0",
        entryId: response.entryId,
        entryDetailType: entrytype,
        questionId: response.questionId,
        responseId: radioGroupValue,
        value_1: radioGroupValue,
      };
    } else {
      if (!entry.entryDetailId) {
        debugLog("Keine DetailId vorhanden, update wird nicht durchgeführt");
        return;
      }
      entry["responseId"] = radioGroupValue;
      entry["value_1"] = radioGroupValue;
    }
    saveChange(entry);
    var lEntries = [...entries];
    lEntries[index] = entry;
    setEntries(lEntries);
  }

  async function handleButtonChange(props, buttonState) {
    var index = entries.findIndex((d) => d.responseId == props.responseid);
    var entry = entries[index];
    if (!entry.entryDetailId) {
      entry = {
        id: props.buttonId,
        entryDetailId: "0",
        entryId: entry.entryId,
        entryDetailType: props.entryType,
        questionId: props.questionid,
        responseId: props.responseid,
      };
    }
    switch (props.button) {
      case "1":
        entry.value_1 = String(buttonState);
        entry.value_3 = entry.value_3 ? String(entry.value_3) : "0";
        // wenn Aufklärung erhalten, darf Kenntnisse nicht mehr entfernt werden
        if (entry.value_3 == "1") {
          entry.value_1 = "1";
        }
        break;
      case "3":
        entry.value_1 = entry.value_1 ? String(entry.value_1) : "0";
        entry.value_3 = String(buttonState);
        // wenn Aufklärung erhalten, gleichzeitig Kenntnisse setzen
        if (buttonState == "1") {
          entry.value_1 = "1";
        }
        break;
    }

    var lEntries = [...entries];
    lEntries[index] = entry;
    setEntries(lEntries);
    saveChange(entry);
    validateKEEntries();
  }

  function validateKEEntries() {
    if (entrytype == 1 || entrytype == 4) {
      return true;
    }
    if (entrytype == 2 && !isVV) {
      return true;
    }
    fetchvalidateKE().then((isKEValidationOk) => {
      setDisableAuswertung(!isKEValidationOk);
    });
  }

  function handleSetProfileActive() {
    kd.validFrom = convertUTCDateToLocalDate(new Date());
    kd.validTo = new Date("2099-12-31");
    kd.active = 1;
    let requestOptions = {
      method: "PUT",
      headers: options.headers,
      body: JSON.stringify(kd),
    };
    reInitToken.then((res) => {
      fetch("/entries/" + entryid, requestOptions).then(function () {
        // Prüfen ob eine andere Profilierung aktiv ist
        requestOptions = {
          method: "GET",
          headers: options.headers,
        };
        const checkActive = async () => {
          if (token.expiresOn < Date.now()) {
            reInitToken();
          }
          const response = await fetch(
            "/entries/" + kd.customerId,
            requestOptions,
          );
          const data = await response.json();
          for (let i = 0; i < data.length; i++) {
            // Es können mehrere Vorsorgeprotfolios mit Pf-Nr -10 aktiviert werden.
            if (
              data[i].active == 1 &&
              data[i].entryId != kd.entryId &&
              data[i].entryType == kd.entryType &&
              data[i].portfolioId == kd.portfolioId &&
              data[i].entryType != 4
            ) {
              data[i].validFrom = kd.createDate;
              data[i].validTo = convertUTCDateToLocalDate(new Date());
              data[i].active = 0;
              requestOptions = {
                method: "PUT",
                headers: options.headers,
                body: JSON.stringify(data[i]),
              };
              if (token.expiresOn < Date.now()) {
                reInitToken();
              }
              fetch("/entries/" + data[i].entryId, requestOptions).then(
                function () {
                  debugLog("");
                },
              );
            }
          }
            navigate("/customerDetail", {
              state: { kunde: location.state.kunde },
          });
        };
        checkActive();
      });
    });
  }

  function getButtonValue(btnIndex, responseid) {
    var ret = 0;
    entries.forEach(function (entry, i) {
      if (entry.responseId == responseid) {
        ret = entry["value_" + btnIndex];
      }
    });
    return ret;
  }

  function initButton() {}

  function getQuestions(lentries) {
    var q = [];
    lentries.forEach(function (item) {
      var i = q.findIndex((x) => x.questionId == item.questionId);
      if (i <= -1) {
        q.push({
          questionId: item.questionId,
          entryType: item.entryType,
          question: item.question,
        });
      }
    });
    return q;
  }

  function getResponses(lentries, lquestions) {
    var actQuestion = lquestions[activeStep];
    var r = [];
    try {
      lentries.forEach(function (item) {
        if (actQuestion.questionId == item.questionId) {
          r.push(item);
        }
      });
    } catch (e) {
      handleReset();
    }
    //debugLog(r);
    return r;
  }

  function isVVSelected(lentries) {
    return isVV;
  }
  function handleInvestWithStandingOrder(e) {
    const value = e.target.value;
    setInvestWithStandingOrder(value);

    // Wenn NEIN → value_2 löschen
    if (value === "0") {
      clearSparplanValue2();
    }
  }

  function clearSparplanValue2() {
    // responses[0] enthält deinen Eintrag
    const response = responses[0];
    const index = entries.findIndex((d) => d.responseId == response.responseId);

    let entry = entries[index];

    if (!entry || !entry.entryDetailId) {
      // Neuer DB-Eintrag
      entry = {
        entryDetailId: "0",
        entryId: response.entryId,
        entryDetailType: entrytype,
        questionId: response.questionId,
        responseId: response.responseId,
        value_1: entry?.value_1 ?? "0",
        value_2: "0", // <-- Hier löschen
        value_3: null,
      };
    } else {
      entry["value_2"] = "0"; // <-- Hier löschen
      entry["value_3"] = null;
    }

    const lEntries = [...entries];
    lEntries[index] = entry;

    setEntries(lEntries);
    saveChange(entry); // <-- Speichern in DB
  }

  function handlePeriodicityChange(e) {
    const p = String(e.target.value ?? "");
    setPeriodicityValue(p);
    saveStandingOrderSettings(
      p,
      bankAccount,
      soExcutionDate,
      startDateValue,
      endDateValue,
    );
  }

  function handleAccountChange(e) {
    const a = String(e.target.value ?? "");
    setBankAccount(a);

    // Wenn ein fixes Konto gewählt wird, sofort speichern
    // (kein Abhängen von isCustomAccount/bankAccount-States beim Speichern)
    if (a !== "99") {
      setCustomAccount(""); // Freitext leeren
      saveStandingOrderSettings(
        periodicityValue,
        a,
        soExcutionDate,
        startDateValue,
        endDateValue,
        customAccount,
      );
    }
    // Wenn "Anderes" gewählt wird, noch NICHT automatisch speichern.
    // Speichern erfolgt über Button "Übernehmen" (oder optional onBlur im Textfeld).
  }

  function handleSODateChange(e) {
    let digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 2);
    if (digitsOnly) {
      const n = Math.max(1, Math.min(31, Number(digitsOnly)));
      digitsOnly = String(n);
    }
    setSoExcutionDate(digitsOnly);
  }

  function handleSOBlur(e) {
    let digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 2);
    if (digitsOnly) {
      const n = Math.max(1, Math.min(31, Number(digitsOnly)));
      digitsOnly = String(n);
    }
    setSoExcutionDate(digitsOnly);
    saveStandingOrderSettings(
      periodicityValue,
      bankAccount,
      digitsOnly,
      startDateValue,
      endDateValue,
      customAccount.trim(),
    );
  }

  // customOverride: optionaler Parameter, um beim Custom-Fall (Button/Blur) den
  // aktuell eingegebenen Freitext explizit zu übergeben (vermeidet stale State).
  function saveStandingOrderSettings(p, a, d, dts, dte, customOverride) {
    if (readOnly) return;

    // Entscheide ausschließlich anhand der übergebenen Parameter,
    // nicht anhand von isCustomAccount/bankAccount (verhindert Race-Conditions).
    const accountToSave = a === "99" ? (customOverride ?? "") : (a ?? "");

    const normalize = (v) => (v == null ? "" : String(v));
    const toStrDate = (v) =>
      v && dayjs.isDayjs(v) ? v.format("YYYY-MM-DD") : normalize(v);

    const parts = [
      normalize(p ?? ""),
      normalize(accountToSave ?? ""),
      normalize(d ?? "28"),
      toStrDate(dts),
      toStrDate(dte),
    ];

    while (parts.length && parts[parts.length - 1] === "") {
      parts.pop();
    }
    const combined = parts.join(";");

    const response = responses[0];
    let index = entries.findIndex((d) => d.responseId == response.responseId);
    let entry = entries[index];

    // Wenn kein Eintrag existiert ODER keine DetailId vorhanden ist → neu initialisieren
    if (!entry || !entry.entryDetailId) {
      entry = {
        entryDetailId: "0",
        entryId: response.entryId,
        entryDetailType: entrytype,
        questionId: response.questionId,
        responseId: response.responseId,
        value_1: entry?.value_1 ?? "0",
        value_2: entry?.value_2 ?? "0",
        value_3: combined,
      };
      // Wenn index -1 war, fügen wir den Eintrag neu in die Liste ein
      if (index === -1) {
        index = entries.length;
      }
    } else {
      // Vorhandenen Datensatz aktualisieren
      entry.value_3 = combined;
    }

    const lEntries = [...entries];
    lEntries[index] = entry;
    setEntries(lEntries);
    saveChange(entry); // persistiert in der DB und triggert refetch()
  }
  function isCustomAccountIncomplete() {
    if (investWithStandingOrder !== "1") return false;

    if (isVVSelected(entries) == false) return false;

    // Ist "Anderes Konto" gewählt?
    const isCustomAccountlocal = bankAccount === "99";
    if (!isCustomAccountlocal) return false;

    // Freitext muss befüllt sein
    if (!customAccount || customAccount.trim() === "") {
      return true;
    }
    const v3 = responses?.[0]?.value_3; // aktuelle Antwort
    if (typeof v3 !== "string" || !v3.includes(";")) {
      return true; //
    }
    const [pSaved, aSaved] = v3.split(";");
    if ((aSaved ?? "").trim() !== customAccount.trim()) {
      return true;
    }
    return false;
  }

  function isStandingOrderSectionValid() {
    if (![15, 25].includes(questions[activeStep].questionId)) {
      return true;
    }
    // Wenn kein Dauerauftrag (Nein) gewählt: Abschnitt ist nicht pflichtig
    if (investWithStandingOrder !== "1") return true;
    if (isVVSelected(entries) == false) return true;
    if (readOnly) return true;

    const amount = getCurrentStandingOrderAmount();
    const hasAmount = amount > 0;

    // Periodizität gewählt?
    const hasPeriodicity = (periodicityValue ?? "") !== "";

    // Belastungskonto gewählt & (falls "Anderes") korrekt gespeichert?
    const hasAccount =
      (bankAccount ?? "") !== "" && !isCustomAccountIncomplete();

    // Gutschrifstkonto gewählt

    return hasAmount && hasPeriodicity && hasAccount;
    }

    function isSparplanOtherPersonVaid() {
        if (![41].includes(questions[activeStep].questionId)) {
            return true;
        }
        const base = responses?.[0];
        if (!base) return 0;
        var idxQuestion = entries.findIndex(
            (d) => d.questionId == base.questionId && d.responseId == 159 && d.entryDetailId,
        );
        var idxDate = entries.findIndex(
            (d) => d.questionId == base.questionId && d.reponseId == 161,
        )
        const entryQ = idxQuestion >= 0 ? entries[idxQuestion] : null;
        const entryD = idxDate >= 0 ? entries[idxDate] : null;
        if (idxQuestion !== null && entryD == null) {
            return false;
        }
        return true;
    }
  function handleSaveCustomAccount() {
    if (readOnly) return;

    // Nur speichern, wenn "Anderes Konto" aktiv ist und ein Freitext vorliegt
    if (bankAccount === "99" && customAccount.trim() !== "") {
      // Wir übergeben den Freitext explizit (customOverride), um stale State zu vermeiden.
      saveStandingOrderSettings(
        periodicityValue,
        "99",
        soExcutionDate,
        startDateValue,
        endDateValue,
        customAccount.trim(),
      );

      // Optional: Fehlermeldung löschen, wenn vorher blockiert wurde
      if (missingMandatoryValue.state) {
        setMissingMandatoryValue({ state: false, text: "" });
      }
    } else {
      // Falls leer: Fehlermeldung setzen
      setMissingMandatoryValue({
        state: true,
        text: "Bitte geben Sie ein Konto ein und speichern Sie es über «Übernehmen».",
      });
    }
  }

  function getCurrentStandingOrderAmount() {
    // responses[0] ist dein „Sparplan“-Datensatz (aktuelle Seite)
    const base = responses?.[0];
    if (!base) return 0;

    const idx = entries.findIndex((d) => d.responseId == base.responseId);
    const entry = idx >= 0 ? entries[idx] : null;
    const v2 = Number(entry?.value_2 ?? 0);
    return Number.isNaN(v2) ? 0 : v2;
  }

  /**
   * **************************************************************
   *
   * KOMPONENTENIMPLEMENTATION
   *
   * **************************************************************
   *
   */

  if (!entries || isLoading || isLoading2 || isLoading3) {
    return <div>Loading...</div>;
  }
  var totalSteps = questions.length;

  return (
    <Container className="mainContainer">
      <Paper>
        <div className="pageContainer">
          <header className="header">
            <Stepper nonLinear activeStep={activeStep} className="bavStepper">
              {questions.map((question, index) => (
                <Step key={question.questionId}>
                  <StepButton onClick={handleStep(index)}></StepButton>
                </Step>
              ))}
            </Stepper>
          </header>
          {renderCurrentStep()}
        </div>
      </Paper>
    </Container>
  );
}
